import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { auth } from '../../../firebase-core';
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence, sendPasswordResetEmail } from 'firebase/auth';
import { signInWithGoogleAuto } from '../../../shared/api/firebase';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { Mail, ChevronLeft } from 'lucide-react';

const LoginModal = ({ isOpen, onClose, onSuccess, onRequestRegister }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const { currentUser } = useAuth();
  const [mode, setMode] = useState('select'); // 'select' | 'email' | 'forgot'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 인증 상태가 생기면 즉시 모달 닫기 (상위에서 마케팅 동의 처리)
  useEffect(() => {
    if (isOpen && currentUser) {
      onSuccess?.(currentUser);
      onClose?.();
    }
  }, [isOpen, currentUser, onSuccess, onClose]);

  // 모달이 열릴 때마다 초기 상태로 리셋
  useEffect(() => {
    if (isOpen) {
      setMode('select');
      setError('');
      setNotice('');
      setFormData({ email: '', password: '' });
      setRememberMe(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
    } catch (err) {
      console.error('로그인 실패:', err);
      setError('로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      // setPersistence는 signInWithGoogleAuto 내부에서 처리될 수 있으나, 일관성을 위해 설정
      await setPersistence(auth, browserLocalPersistence);
      await signInWithGoogleAuto(true); // rememberMe 값을 true로 고정
    } catch (error) {
      if (error.message === 'popup-blocked-cors') {
        // 사용자가 팝업을 닫은 경우: 조용히 무시
        return;
      }
      setError('구글 로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setNotice('');
    try {
      if (!formData.email) {
        setError('이메일을 입력하세요.');
        return;
      }
      await sendPasswordResetEmail(auth, formData.email);
      setNotice('비밀번호 재설정 이메일을 전송했습니다. 메일함을 확인하세요.');
    } catch (err) {
      console.error('재설정 메일 전송 실패:', err);
      const code = err?.code || '';
      if (code === 'auth/user-not-found') {
        setError('해당 이메일의 계정을 찾을 수 없습니다.');
      } else if (code === 'auth/invalid-email') {
        setError('올바른 이메일 형식인지 확인해주세요.');
      } else {
        setError('재설정 메일 전송에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const headerTitle = mode === 'email' ? '이메일로 로그인' : mode === 'forgot' ? '비밀번호 재설정' : '로그인';
  const header = (
    <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
      <p className="modal-title">{headerTitle}</p>
    </div>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={undefined} showCloseButton>
        {mode !== 'select' && (
          <div className="modal-header">
            <button
                type="button"
                className="modal-back-btn"
                aria-label="뒤로"
                onClick={() => { setError(''); setMode('select'); }}
                style={{ right: 'auto', left: 'var(--spacing-md)' }}
            >
              <ChevronLeft />
            </button>
          </div>

        )}
        {header}

        {mode === 'select' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <button
              type="button"
              className="btn-simple large"
              style={{ width: '100%' }}
              onClick={() => setMode('email')}
            >
              <Mail size={18} style={{ marginRight: 8 }} />
              이메일로 로그인
            </button>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="btn-ghost large"
              style={{ width: '100%' }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 18,
                  height: 18,
                  display: 'inline-block',
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='18' height='18' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z' fill='%234285F4'/%3E%3Cpath d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z' fill='%2334A853'/%3E%3Cpath d='M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z' fill='%23FBBC05'/%3E%3Cpath d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z' fill='%23EA4335'/%3E%3C/g%3E%3C/svg%3E\")",
                  backgroundSize: '18px 18px',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  marginRight: 8
                }}
              />
              Google로 로그인
            </button>
          </div>
        )}

        {mode === 'email' && (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">이메일</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="이메일을 입력하세요"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">비밀번호</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-input"
                placeholder="비밀번호를 입력하세요"
                required
              />
            </div>

            <div className="form-options" style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <label className="form-checkbox-container">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="form-checkbox-text">로그인 상태 유지</span>
              </label>
              <a href="#forgot" className="btn-link" onClick={(e) => { e.preventDefault(); setError(''); setNotice(''); setMode('forgot'); }}>비밀번호를 잊으셨나요?</a>
            </div>

            <button
              type="submit"
              className="btn-primary large"
              disabled={isLoading}
            >
              {isLoading ? '로그인 중...' : '로그인'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleSendReset} className="auth-form">
            <div className="form-group">
              <label htmlFor="reset-email" className="form-label">이메일</label>
              <input
                type="email"
                id="reset-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="가입한 이메일을 입력하세요"
                required
              />
            </div>

            <button
              type="submit"
              className="btn-primary large"
              disabled={isLoading}
            >
              {isLoading ? '전송 중...' : '재설정 이메일 보내기'}
            </button>

            {notice && <div className="form-message-success" style={{ marginTop: 'var(--spacing-md)' }}>{notice}</div>}
          </form>
        )}

        {error && <div className="form-message-error">{error}</div>}

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-md)', paddingTop: 'var(--spacing-md)', borderTop: '1px solid var(--gray-200)' }}>
            <p style={{ fontSize: 'var(--font-base)', color: 'var(--gray-600)', margin: 0 }}>
                아직 계정이 없으신가요? <a href="/register" className="btn-link" onClick={(e) => { e.preventDefault(); onRequestRegister?.(); }}>회원가입</a>
            </p>
        </div>
      </Modal>
    </>
  );
};

export default LoginModal;


